import { Link } from 'react-router-dom';
import {
  kbSections,
  officialLinks,
  knowledgeBaseFaqs,
  entityDisambiguation,
} from '@/mocks/knowledgeBase';

const sectionNav = [
  ...kbSections.map((section) => ({
    slug: section.slug,
    title: section.title,
    icon: section.icon,
  })),
  { slug: 'official-links', title: 'Official Gracie & Lily Links', icon: 'ri-links-line' },
  { slug: 'faq', title: 'Frequently Asked Questions', icon: 'ri-question-answer-line' },
];

const highlightFaqs = knowledgeBaseFaqs.slice(0, 8);

export default function KnowledgeBaseSection() {
  return (
    <section
      id="knowledge-base"
      aria-labelledby="home-knowledge-base-heading"
      className="w-full bg-background-100 py-10 md:py-20 border-t border-background-200 scroll-mt-20"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-center gap-3 mb-3">
          <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-book-2-line text-xl"></i>
          </span>
          <p className="text-secondary-600 text-xs font-bold uppercase tracking-widest">
            Knowledge Base
          </p>
        </div>

        <h2
          id="home-knowledge-base-heading"
          className="text-center text-3xl md:text-4xl font-black text-primary-500 mb-3"
        >
          Gracie &amp; Lily Adventures Knowledge Base
        </h2>
        <p className="text-center text-foreground-700 text-base md:text-lg mb-6 max-w-3xl mx-auto">
          Everything about Gracie &amp; Lily Adventures in one place &mdash; the brand, the Bichon
          Frise sisters Gracie and Lily, the children&rsquo;s stories, values, books, journals,
          gifts and official links.
        </p>

        <div className="max-w-4xl mx-auto rounded-xl bg-secondary-50 border border-secondary-200 px-4 py-4 md:px-5 mb-8">
          <p className="text-sm text-foreground-800 leading-relaxed text-center">
            <strong className="text-foreground-950">Entity note:</strong> {entityDisambiguation}
          </p>
        </div>

        {/* Section directory */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          {sectionNav.map((item) => (
            <Link
              key={item.slug}
              to={`/knowledge-base#${item.slug}`}
              className="group flex items-center gap-3 bg-white border border-background-200 rounded-xl px-4 py-3 hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                <i className={`${item.icon} text-lg`}></i>
              </span>
              <span className="flex-1 text-sm font-bold text-foreground-950 text-left">
                {item.title}
              </span>
              <span className="w-5 h-5 flex items-center justify-center text-foreground-500 group-hover:text-primary-600 transition-colors">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          ))}
        </div>

        {/* Quick answers */}
        <div className="max-w-4xl mx-auto">
          <h3 className="text-lg md:text-xl font-extrabold text-primary-500 mb-4 text-center">
            Popular Questions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {highlightFaqs.map((faq) => (
              <details
                key={faq.question}
                className="bg-white border border-background-200 rounded-xl overflow-hidden"
              >
                <summary className="flex items-start gap-3 px-4 py-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                  <h4 className="flex-1 text-sm font-bold text-foreground-950 text-left">
                    {faq.question}
                  </h4>
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-600 shrink-0">
                    <i className="ri-add-line text-lg"></i>
                  </span>
                </summary>
                <p className="text-sm text-foreground-700 leading-relaxed px-4 pb-4">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>

        {/* Official links */}
        <div className="mt-8 max-w-4xl mx-auto">
          <h3 className="text-lg md:text-xl font-extrabold text-primary-500 mb-4 text-center">
            Official Gracie &amp; Lily Links
          </h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {officialLinks.map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-white border border-background-200 rounded-xl px-4 py-3 h-full hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                    <i className={`${link.icon} text-lg`}></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-foreground-950">
                      {link.label}
                    </span>
                    <span className="block text-xs text-foreground-600 truncate">
                      {link.description}
                    </span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/knowledge-base"
            className="inline-flex items-center gap-2 bg-primary-500 text-white px-6 md:px-7 py-3 rounded-lg font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-700 transition-colors"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-book-2-line text-lg"></i>
            </span>
            Explore the Full Knowledge Base
          </Link>
        </div>
      </div>
    </section>
  );
}